/* eslint-disable max-lines */
import { addMonths, addWeeks, addYears, isValid, parse } from 'date-fns';

import { formatDate } from 'lib/helpers/dateUtils';

export const DEFAULT_RECURRING_PAYMENT = (timezone) => ({
  type: 'auto-recurring',
  repeatEvent: 'monthly',
  repeatEvery: 1,
  chargeTime: 'immediately',
  startDate: formatDate(new Date(), timezone, 'MM/dd/yyyy'),
  linkToRecurringAppointment: null,
  onEvent: 'day',
  weekEvent: null,
  // Same rule as the pre-auth "charge on" date option: only the month's actual last day
  // is stored as 'last', so the matching radio starts selected.
  dayEvent: (() => {
    const d = Number(formatDate(new Date(), timezone, 'd'));
    const month = Number(formatDate(new Date(), timezone, 'M'));
    const year = Number(formatDate(new Date(), timezone, 'yyyy'));
    return d === new Date(year, month, 0).getDate() ? 'last' : d;
  })(),
  ends: 'never',
  endsDate: null,
  preAuthRequired: true
});

export const DEFAULT_INSTALLMENTS_PAYMENT = (timezone) => ({
  type: 'installments',
  paymentType: 'number-of-payments',
  amount: null,
  upfrontPayment: 0,
  startDate: formatDate(new Date(), timezone, 'MM/dd/yyyy'),
  repeatEvent: 'monthly',
  repeatEvery: 1,
  numberOfPayments: 1,
  amountPerPayment: null,
  preAuthRequired: true
});

export const REPEAT_EVENT_OPTIONS = [
  { value: 'weekly', label: 'Weekly', unitLabel: 'Week' },
  { value: 'monthly', label: 'Monthly', unitLabel: 'Month' },
  { value: 'yearly', label: 'Yearly', unitLabel: 'Year' }
];

export const PAYMENT_TYPE_OPTIONS = [
  { value: 'number-of-payments', label: 'Number of payments' },
  { value: 'amount-per-payment', label: 'Amount per payment' }
];

export const PLAN_TYPE_OPTIONS = [
  { value: 'installments', label: 'Installments' },
  { value: 'auto-recurring', label: 'Auto-recurring' }
];

export const CHARGE_TIME_OPTIONS = [
  { value: 'immediately', label: 'Immediately' },
  { value: 'future-date', label: 'Future date' }
];

export const ENDS_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: 'on-date', label: 'By' }
];

export const generateOnEventOptions = (currentRepeatEvent) => {
  const currentYear = new Date().getFullYear();

  // Values are JS weekday indexes (0 = Sunday), so the labels must come from a fixed
  // Sunday-anchored week rather than a date whose weekday changes by year.
  if (currentRepeatEvent === 'weekly') return WEEKDAY_OPTIONS;

  if (currentRepeatEvent === 'yearly') {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: formatDate(new Date(currentYear, i, 1), null, 'MMMM')
    }));
  }

  return [
    { value: 'day', label: 'Day' },
    { value: 'first', label: 'First' },
    { value: 'second', label: 'Second' },
    { value: 'third', label: 'Third' },
    { value: 'fourth', label: 'Fourth' },
    { value: 'last', label: 'Last' }
  ];
};

// Anchor: 2024-01-01 is a Monday; Jan 0 (Dec 31, 2023) is Sunday → use a fixed
// 7-day window so weekday labels are stable across years.
export const WEEKDAY_OPTIONS = (() => {
  const anchorSunday = new Date(2023, 11, 31);
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(anchorSunday);
    date.setDate(anchorSunday.getDate() + i);
    return { value: i, label: formatDate(date, null, 'EEEE') };
  });
})();

export const isOccurrenceOnEvent = (onEvent) =>
  ['first', 'second', 'third', 'fourth', 'last'].includes(onEvent);

export const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const computeEndDate = (startDate, totalPayments, repeatEvent, repeatEvery = 1) => {
  if (!startDate || !totalPayments || totalPayments < 1) return null;

  let parsed;
  if (startDate instanceof Date) {
    parsed = startDate;
  } else if (typeof startDate === 'string') {
    const isoMatch = startDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    parsed = isoMatch
      ? new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]))
      : new Date(startDate);
  } else {
    parsed = new Date(startDate);
  }
  if (isNaN(parsed.getTime())) return null;

  const intervals = (totalPayments - 1) * repeatEvery;

  let endDate;
  if (repeatEvent === 'weekly') endDate = addWeeks(parsed, intervals);
  else if (repeatEvent === 'yearly') endDate = addYears(parsed, intervals);
  else endDate = addMonths(parsed, intervals);

  return formatDate(endDate, null, 'MMMM d, yyyy');
};

const OCCURRENCES = ['first', 'second', 'third', 'fourth', 'last'];

const isBlank = (value) => value == null || value === '';

const isWeekdayIndex = (value) => {
  if (isBlank(value)) return false;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 6;
};

// `0` is how the API stores 'last' (day_event), so an edited plan comes back with it.
const isDayOfMonth = (value) => {
  if (value === 'last') return true;
  if (isBlank(value)) return false;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 && n <= 31;
};

// Mirrors the backend's on/day/week event checks (createPaymentPlan, validateRecurringEvents)
// so a plan the API would reject never reaches it.
export const getScheduleEventsError = (recurringPayment) => {
  const { repeatEvent, onEvent, dayEvent, weekEvent } = recurringPayment || {};

  if (!['weekly', 'monthly', 'yearly'].includes(repeatEvent)) return 'Repeat frequency is required';
  if (weekEvent && !OCCURRENCES.includes(weekEvent)) return 'Week of the month is invalid';

  if (repeatEvent === 'weekly') {
    return isWeekdayIndex(onEvent) ? null : 'Day of week is required';
  }

  if (repeatEvent === 'yearly') {
    const month = Number(onEvent);
    if (isBlank(onEvent) || !Number.isInteger(month) || month < 1 || month > 12) {
      return 'Month is required';
    }
    if (weekEvent) return isWeekdayIndex(dayEvent) ? null : 'Day of week is required';
    return isDayOfMonth(dayEvent) && dayEvent !== 0 ? null : 'Day of the month is required';
  }

  const isOccurrence = !!weekEvent || OCCURRENCES.includes(onEvent);
  if (isOccurrence) return isWeekdayIndex(dayEvent) ? null : 'Day of week is required';
  if (onEvent === 'day') return isDayOfMonth(dayEvent) ? null : 'Day of the month is required';

  const dayOfMonth = Number(onEvent);
  if (isBlank(onEvent) || !Number.isInteger(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) {
    return 'Charge day is required';
  }
  return null;
};

export const getRecurringValidationError = (recurringPayment, totalAmount) => {
  if (!recurringPayment) return null;

  const amount = Number(totalAmount) || 0;

  if (recurringPayment.type === 'installments') {
    const upfront = Number(recurringPayment.upfrontPayment) || 0;
    const remaining = amount - upfront;

    if (upfront > 0 && upfront > amount) {
      return 'Upfront payment cannot exceed the total amount';
    }

    if (remaining <= 0) {
      return upfront > 0
        ? 'Upfront payment cannot equal the total amount — leave a balance to schedule'
        : 'Total amount must be greater than $0';
    }

    if (!recurringPayment.repeatEvent) {
      return 'Repeat frequency is required';
    }
    if (!Number(recurringPayment.repeatEvery) || Number(recurringPayment.repeatEvery) < 1) {
      return 'Repeat every must be at least 1';
    }

    const { paymentType, numberOfPayments, amountPerPayment } = recurringPayment;

    if (paymentType === 'number-of-payments') {
      if (!numberOfPayments || numberOfPayments < 1) {
        return 'Number of payments must be at least 1';
      }
    }

    if (paymentType === 'amount-per-payment') {
      if (!amountPerPayment || Number(amountPerPayment) <= 0) {
        return 'Amount per payment must be greater than $0';
      }
      if (Number(amountPerPayment) > remaining) {
        return 'Amount per payment cannot exceed the remaining balance';
      }
    }

    if (!recurringPayment.startDate) {
      return 'Start date is required';
    }
  }

  if (recurringPayment.type === 'auto-recurring') {
    if (amount <= 0) {
      return 'Payment amount must be greater than $0';
    }

    if (recurringPayment.chargeTime === 'future-date') {
      if (!recurringPayment.startDate) return 'Start date is required for future-dated plans';
      const parsedStart = parse(recurringPayment.startDate, 'MM/dd/yyyy', new Date());
      if (!isValid(parsedStart)) return 'Start date is invalid';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (parsedStart <= today) return 'Start date must be in the future';
    }

    const scheduleError = getScheduleEventsError(recurringPayment);
    if (scheduleError) return scheduleError;

    if (recurringPayment.ends === 'on-date') {
      if (!recurringPayment.endsDate) {
        return 'End date is required';
      }
      if (recurringPayment.startDate && recurringPayment.endsDate) {
        const parsedStartDate = parse(recurringPayment.startDate, 'MM/dd/yyyy', new Date());
        const parsedEndsDate = parse(recurringPayment.endsDate, 'MM/dd/yyyy', new Date());
        if (
          !isValid(parsedStartDate) ||
          !isValid(parsedEndsDate) ||
          parsedEndsDate <= parsedStartDate
        ) {
          return 'End date must be after the start date';
        }
      }
    }
  }

  return null;
};

export const generateDayEventOptions = (onEvent = 1) => {
  if (isOccurrenceOnEvent(onEvent)) {
    return WEEKDAY_OPTIONS;
  }

  const isValidMonth = typeof onEvent === 'number' && onEvent >= 1 && onEvent <= 12;

  if (isValidMonth) {
    const year = new Date().getFullYear();
    const days = new Date(year, onEvent, 0).getDate();
    return Array.from({ length: days }, (_, i) => ({
      value: i + 1,
      label: getOrdinal(i + 1)
    }));
  }

  return [
    ...Array.from({ length: 28 }, (_, i) => ({
      value: i + 1,
      label: getOrdinal(i + 1)
    })),
    { value: 'last', label: 'Last' }
  ];
};

export const formatAppointmentRecurrence = (appointment) => {
  const recurrence = appointment?.recurrence;
  const startsAt = appointment?.starts_at;
  if (!recurrence || !startsAt) return '';

  const date = new Date(startsAt);
  const time = formatDate(startsAt, null, 'h:mma');
  const every = recurrence.repeat_every || 1;

  switch (Number(recurrence.repeat_event)) {
    case 3:
      return `Every ${every > 1 ? `${every} days` : 'day'} at ${time}`;
    case 2: {
      const days = Array.isArray(recurrence.weekdays) ? recurrence.weekdays.join(', ') : '';
      return `Every ${every > 1 ? `${every} weeks` : 'week'}${days ? ` on ${days}` : ''} at ${time}`;
    }
    case 1:
      return `Every ${every > 1 ? `${every} months` : 'month'} on the ${getOrdinal(date.getDate())} day at ${time}`;
    case 0:
      return `Every ${every > 1 ? `${every} years` : 'year'} on ${formatDate(startsAt, null, 'MMMM d')} at ${time}`;
    default:
      return formatDate(startsAt);
  }
};

export const formatPractitionerName = (practitioner) => {
  if (!practitioner) return '';
  const { title, prefix, f_name, l_name, suffix } = practitioner;
  return [prefix || title, f_name, l_name, suffix].filter(Boolean).join(' ');
};

const APPT_FREQ_TO_PLAN = { 3: null, 2: 'weekly', 1: 'monthly', 0: 'yearly' };

const WEEKDAY_NAME_TO_INDEX = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
};

const WEEK_OF_MONTH_LABELS = ['first', 'second', 'third', 'fourth', 'last'];

const getWeekOfMonthLabel = (date) => {
  const day = date.getDate();
  const week = Math.ceil(day / 7);
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  if (day + 7 > daysInMonth) return 'last';
  return WEEK_OF_MONTH_LABELS[week - 1] || 'first';
};

export const recurringPaymentFromAppointment = (appointment, timezone, current = {}) => {
  const recurrence = appointment?.recurrence;
  const startsAt = appointment?.starts_at;
  if (!recurrence || !startsAt) {
    return { ...current, linkToRecurringAppointment: appointment?.id ?? null };
  }

  const date = new Date(startsAt);
  const repeatEvent = APPT_FREQ_TO_PLAN[Number(recurrence.repeat_event)];
  const repeatEvery = recurrence.repeat_every || 1;

  let onEvent = current.onEvent;
  let dayEvent = current.dayEvent;

  if (repeatEvent === 'weekly') {
    const first = Array.isArray(recurrence.weekdays) ? recurrence.weekdays[0] : null;
    const name = first?.value || first;
    onEvent = WEEKDAY_NAME_TO_INDEX[name] ?? date.getDay();
  } else if (repeatEvent === 'monthly') {
    if (recurrence.repeat_on === 'week') {
      onEvent = getWeekOfMonthLabel(date);
      dayEvent = date.getDay();
    } else {
      const d = date.getDate();
      onEvent = 'day';
      dayEvent = d > 28 ? 'last' : d;
    }
  } else if (repeatEvent === 'yearly') {
    onEvent = date.getMonth() + 1;
    const d = date.getDate();
    dayEvent = d > 28 ? 'last' : d;
  }

  return {
    ...current,
    linkToRecurringAppointment: appointment?.id ?? null,
    startDate: formatDate(startsAt, timezone, 'MM/dd/yyyy'),
    repeatEvent: repeatEvent || current.repeatEvent,
    repeatEvery,
    onEvent,
    dayEvent,
    ends: recurrence.end_date ? 'on-date' : 'never',
    endsDate: recurrence.end_date || null
  };
};

export const recurringAppointmentOptionLabel = (appointment) => {
  const subtitle = [
    formatAppointmentRecurrence(appointment),
    formatPractitionerName(appointment?.practitioner)
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <span className="flex w-full flex-col">
      <span className="font-500">Appointment #{appointment?.id}</span>
      <span className="text-sm text-neutral-600">{subtitle}</span>
    </span>
  );
};
